/**
 * Returns Service - API client for returns management
 * 
 * This service provides a comprehensive interface for managing product returns
 * across three main workflows:
 * 1. Blind Returns - Processing returns without prior order information using AI vision
 * 2. RMA (Return Merchandise Authorization) - Formal return requests from customers
 * 3. Returns Forecasting - Predictive analytics and scheduling for incoming returns
 * 
 * All methods use the centralized API client with JWT authentication, retry logic,
 * and offline queue support.
 * 
 * @module services/returnsService
 */

import { api } from './api.js';
import { API_ENDPOINTS } from '../config/api.config.js';

const returnsService = {
  
  /**
   * Create a new blind return record
   * @param {Object} data - Blind return data
   * @param {string} data.warehouse_id - Warehouse identifier
   * @param {string} data.operator_id - Operator processing the return
   * @param {string} [data.carrier] - Shipping carrier name
   * @param {string} [data.tracking_number] - Tracking number if available
   * @param {number} [data.weight] - Package weight in kg
   * @param {Object} [data.dimensions] - Package dimensions {length, width, height} in cm
   * @param {string} [data.condition] - Package condition (good, damaged, opened, sealed)
   * @param {string} [data.notes] - Additional notes
   * @returns {Promise<Object>} Created blind return with id
   */
  createBlindReturn: async (data) => {
    const response = await api.post(`${API_ENDPOINTS.RETURNS}/blind-returns`, data);
    return response.data;
  },

  /**
   * Upload photos for blind return identification
   * @param {string|number} blindReturnId - Blind return identifier
   * @param {File[]} photos - Array of photo File objects
   * @param {string} [photoType='package'] - Type of photos (package, label, damage)
   * @returns {Promise<Object>} Upload confirmation with photo URLs
   */
  uploadBlindReturnPhotos: async (blindReturnId, photos, photoType = 'package') => {
    const formData = new FormData();
    photos.forEach(photo => formData.append('photos', photo));
    formData.append('photo_type', photoType);
    
    const response = await api.upload(
      `${API_ENDPOINTS.RETURNS}/blind-returns/${blindReturnId}/upload-photos`,
      formData
    );
    return response.data;
  },

  /**
   * Trigger AI identification for blind return
   * @param {string|number} blindReturnId - Blind return identifier
   * @param {string} [method='ai_vision'] - Identification method
   * @returns {Promise<Object>} Identification results with suggested orders
   */
  identifyBlindReturn: async (blindReturnId, method = 'ai_vision') => {
    const response = await api.post(
      `${API_ENDPOINTS.RETURNS}/blind-returns/${blindReturnId}/identify`,
      { method }
    );
    return response.data;
  },

  /**
   * Link blind return to identified order
   * @param {string|number} blindReturnId - Blind return identifier
   * @param {number} orderId - Order ID to link to
   * @param {number} [confidence] - Confidence score (0-1)
   * @param {string} [notes] - Linking notes
   * @returns {Promise<Object>} Updated blind return with order link
   */
  linkBlindReturnToOrder: async (blindReturnId, orderId, confidence, notes) => {
    const response = await api.post(
      `${API_ENDPOINTS.RETURNS}/blind-returns/${blindReturnId}/link-to-order`,
      { order_id: orderId, confidence, notes }
    );
    return response.data;
  },

  /**
   * Get list of blind returns with optional filters
   * @param {Object} [params={}] - Query parameters
   * @returns {Promise<Object>} Paginated list of blind returns
   */
  getBlindReturns: async (params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.RETURNS}/blind-returns`, { params });
    return response.data;
  },

  /**
   * Get detailed information for a specific blind return
   * @param {string|number} blindReturnId - Blind return identifier
   * @returns {Promise<Object>} Blind return details with photos and identification results
   */
  getBlindReturnDetails: async (blindReturnId) => {
    const response = await api.get(`${API_ENDPOINTS.RETURNS}/blind-returns/${blindReturnId}`);
    return response.data;
  },

  
  createRMA: async (data) => {
    const response = await api.post(`${API_ENDPOINTS.RETURNS}/rma`, data);
    return response.data;
  },

  getRMARequests: async (params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.RETURNS}/rma`, { params });
    return response.data;
  },

  getRMADetails: async (rmaNumber) => {
    const response = await api.get(`${API_ENDPOINTS.RETURNS}/rma/${rmaNumber}`);
    return response.data;
  },

  approveRMA: async (rmaNumber, data) => {
    const response = await api.post(`${API_ENDPOINTS.RETURNS}/rma/${rmaNumber}/approve`, data);
    return response.data;
  },

  rejectRMA: async (rmaNumber, reason) => {
    const response = await api.post(`${API_ENDPOINTS.RETURNS}/rma/${rmaNumber}/reject`, { reason });
    return response.data;
  },

  generateReturnLabel: async (rmaNumber) => {
    const response = await api.post(`${API_ENDPOINTS.RETURNS}/rma/${rmaNumber}/generate-label`);
    return response.data;
  },

  receiveRMAReturn: async (rmaNumber, data) => {
    const response = await api.post(`${API_ENDPOINTS.RETURNS}/rma/${rmaNumber}/receive`, data);
    return response.data;
  },

  
  getReturns: async (params = {}) => {
    const response = await api.get(API_ENDPOINTS.RETURNS, { params });
    return response.data;
  },

  getReturn: async (returnId) => {
    const response = await api.get(`${API_ENDPOINTS.RETURNS}/${returnId}`);
    return response.data;
  },

  createReturn: async (data) => {
    const response = await api.post(API_ENDPOINTS.RETURNS, data);
    return response.data;
  },

  updateReturn: async (returnId, data) => {
    const response = await api.put(`${API_ENDPOINTS.RETURNS}/${returnId}`, data);
    return response.data;
  },

  deleteReturn: async (returnId) => {
    const response = await api.delete(`${API_ENDPOINTS.RETURNS}/${returnId}`);
    return response.data;
  },

  processReturn: async (returnId) => {
    const response = await api.post(`${API_ENDPOINTS.RETURNS}/${returnId}/process`);
    return response.data;
  },

  completeReturnWorkflow: async (returnId) => {
    const response = await api.post(`${API_ENDPOINTS.RETURNS}/${returnId}/complete-workflow`);
    return response.data;
  },

  qualityControlReturn: async (returnId) => {
    const response = await api.post(`${API_ENDPOINTS.RETURNS}/${returnId}/quality-control`);
    return response.data;
  },

  restockReturn: async (returnId, restockData) => {
    const response = await api.post(`${API_ENDPOINTS.RETURNS}/${returnId}/restock`, restockData);
    return response.data;
  },

  processRefund: async (returnId, refundData) => {
    const response = await api.post(`${API_ENDPOINTS.RETURNS}/${returnId}/refund`, refundData);
    return response.data;
  },

  batchProcessReturns: async (returnIds) => {
    const response = await api.post(`${API_ENDPOINTS.RETURNS}/batch/process`, { return_ids: returnIds });
    return response.data;
  },

  
  getReturnReasons: async (params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.RETURNS}/reasons`, { params });
    return response.data;
  },

  
  getReturnsForecast: async (dateRange) => {
    const response = await api.get(`${API_ENDPOINTS.RETURNS}/forecast`, {
      params: {
        date_from: dateRange.from,
        date_to: dateRange.to,
      }
    });
    return response.data;
  },

  getDailyForecast: async (warehouseId, forecastDate) => {
    const response = await api.get(
      `${API_ENDPOINTS.RETURNS}/incoming/forecast/${warehouseId}/${forecastDate}`
    );
    return response.data;
  },

  generateForecast: async (warehouseId, forecastDate) => {
    const response = await api.post(`${API_ENDPOINTS.RETURNS}/incoming/forecast/generate`, null, {
      params: { warehouse_id: warehouseId, forecast_date: forecastDate }
    });
    return response.data;
  },

  getDailyReturnsSchedule: async (warehouseId, scheduleDate, shift = null) => {
    const response = await api.get(
      `${API_ENDPOINTS.RETURNS}/incoming/schedule/${warehouseId}/${scheduleDate}`,
      { params: shift ? { shift } : {} }
    );
    return response.data;
  },

  generateWeeklySchedule: async (warehouseId, startDate, days = 7) => {
    const response = await api.post(`${API_ENDPOINTS.RETURNS}/incoming/schedule/generate`, null, {
      params: { warehouse_id: warehouseId, start_date: startDate, days }
    });
    return response.data;
  },

  updateScheduleActuals: async (scheduleId, actualsData) => {
    const response = await api.put(
      `${API_ENDPOINTS.RETURNS}/incoming/schedule/${scheduleId}/actuals`,
      null,
      { params: actualsData }
    );
    return response.data;
  },

  createDailySchedule: async (data) => {
    const response = await api.post(`${API_ENDPOINTS.RETURNS}/schedule/daily`, data);
    return response.data;
  },

  
  getIncomingReturns: async (params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.RETURNS}/incoming/tracking`, { params });
    return response.data;
  },

  syncRMATracking: async () => {
    const response = await api.post(`${API_ENDPOINTS.RETURNS}/incoming/tracking/sync-rma`);
    return response.data;
  },

  updateCarrierTracking: async (trackingNumber, carrierData) => {
    const response = await api.post(
      `${API_ENDPOINTS.RETURNS}/incoming/tracking/${trackingNumber}/update`,
      carrierData
    );
    return response.data;
  },

  detectReturnsFromShipments: async () => {
    const response = await api.post(`${API_ENDPOINTS.RETURNS}/incoming/tracking/detect-returns`);
    return response.data;
  },

  markReturnReceived: async (trackingNumber, data) => {
    const response = await api.post(
      `${API_ENDPOINTS.RETURNS}/incoming/tracking/${trackingNumber}/received`,
      data
    );
    return response.data;
  },

  
  getOperatorDashboard: async (operatorId, warehouseId, date) => {
    const response = await api.get(`${API_ENDPOINTS.RETURNS}/dashboard/operator`, {
      params: { operator_id: operatorId, warehouse_id: warehouseId, date }
    });
    return response.data;
  },

  getManagerDashboard: async (warehouseId, dateFrom, dateTo) => {
    const response = await api.get(`${API_ENDPOINTS.RETURNS}/dashboard/manager`, {
      params: { warehouse_id: warehouseId, date_from: dateFrom, date_to: dateTo }
    });
    return response.data;
  },

  
  getMetrics: async () => {
    const response = await api.get(`${API_ENDPOINTS.RETURNS}/metrics`);
    return response.data;
  },

  getAnalytics: async (params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.RETURNS}/analytics`, { params });
    return response.data;
  },

  getFilters: async () => {
    const response = await api.get(`${API_ENDPOINTS.RETURNS}/filters`);
    return response.data;
  },

  exportReturns: async (params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.RETURNS}/export`, {
      params,
      responseType: 'blob'
    });
    return response.data;
  },
};

export default returnsService;
